import React, { useCallback, useEffect, useState } from "react";
import { debounce } from "lodash";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";

import { getSellerProducts, updateProducts } from "api/products";
import * as XLSX from "xlsx";

import { Button, InputContainer, TextInput } from "components/Form";
import { FormattedTextField } from "components/FormElements";
import PageTitle from "components/PageTitle";
import DefaultTable from "components/Table";

import { useLayoutDispatch, popupNotification } from "context/LayoutContext";
import { useUserState } from "context/UserContext";

import {
  CubeTransparent,
  Download2,
  ExportIcon,
  FolderSpecial,
  ImportIcon,
  MergeType,
  QrCode2,
  Scale,
} from "icons";

import { initialAPIListValue } from "utils/constants";
import { convertDataToTableView } from "utils/convert";
import { exportToExcel } from "utils/exportToExcel";
import getClearAdornment from "utils/form";
import parseJwt from "utils/jwt";
import { getWithExpiry } from "utils/storage";

const cells = [
  {
    id: "sku",
    label: "product_sku",
    sortable: true,
    render: (r, exported) => r.sku,
  },
  {
    id: "inventory_qty",
    label: "qty",
    render: (r, exported) => r.inventory_qty || "-",
  },
  {
    id: "upc",
    label: "upc",
    render: (r, exported) => r.upc || "-",
  },
  {
    id: "boxes__length",
    label: "L x W x H",
    labelTextTransform: "none",
    render: (row, exported) =>
      row?.boxes.length && row?.boxes?.[0]["length"]
        ? `${row.boxes[0]["length"]} X ${row.boxes[0]["width"]} X ${row.boxes[0]["height"]}`
        : "-",
  },
  {
    id: "weight",
    label: "Weight",
    sortable: false,
    render: (row, exported) => (parseInt(row.weight) ? row.weight : "-"),
  },
  {
    id: "size",
    label: "Size",
    sortable: false,
    render: (row, exported) => row.short_size || "-",
  },
];

const exportCells = [
  {
    id: "sku",
    label: "product_sku",
    exportLabel: "SKU",
    sortable: true,
    render: (r, exported) => r.sku,
  },
  {
    id: "upc",
    label: "upc",
    exportLabel: "UPC",
    render: (r, exported) => r.upc || "-",
  },
  {
    id: "boxes__length",
    exportLabel: "Length",
    labelTextTransform: "none",
    render: (row, exported) =>
      (row?.boxes?.length && row?.boxes?.[0]["length"]) || "-",
  },
  {
    id: "boxes__width",
    exportLabel: "Width",
    labelTextTransform: "none",
    render: (row, exported) =>
      (row?.boxes?.length && row?.boxes?.[0]["width"]) || "-",
  },
  {
    id: "boxes__height",
    exportLabel: "Height",
    labelTextTransform: "none",
    render: (row, exported) =>
      (row?.boxes?.length && row?.boxes?.[0]["height"]) || "-",
  },
  {
    id: "weight",
    label: "Weight",
    sortable: false,
    render: (row, exported) => (parseInt(row.weight) ? row.weight : "-"),
  },
  {
    id: "size",
    label: "Size",
    sortable: false,
    render: (row, exported) => row.short_size || "-",
  },
  {
    id: "inventory_qty",
    label: "qty",
    exportLabel: "QTY",
    render: (r, exported) => r.inventory_qty || "-",
  },
];

export default function Product(props) {
  const { t } = useTranslation();
  const [seller_id] = useState(2);
  const layoutDispatch = useLayoutDispatch();
  const [error, setError] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [products, setProducts] = useState(initialAPIListValue);
  const [search, setSearch] = useState({
    sku: "",
    upc: "",
    seller_id: 2,
  });
  const [orderDirection, setOrderDirection] = useState("asc");
  const [orderBy, setOrderBy] = useState("sku");
  const [exportDisabled, setExportDisabled] = useState(false);

  const { permissions } = useUserState();
  const [headerCounts, setHeaderCounts] = useState({});

  const [productData, setProductData] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [product, setProduct] = useState(null);

  useEffect(() => {
    if (products.isFetching) {
      let params = {
        limit: rowsPerPage,
        offset: rowsPerPage * page,
        ...search,
      };
      if (orderDirection && orderBy) {
        params.ordering = `${orderDirection === "asc" ? "" : "-"}${orderBy}`;
      }
      getSellerProducts({
        params,
        setError,
        setFunc: (res) => {
          setProducts(res);
          setHeaderCounts(res.customCounts);
        },
        paginated: true,
      });
    }
  }, [
    rowsPerPage,
    page,
    search,
    products.isFetching,
    orderBy,
    orderDirection,
  ]);

  useEffect(() => {
    if (error) {
      popupNotification({
        dispatch: layoutDispatch,
        message: error,
      });
      setError(false);
    }
  }, [error, layoutDispatch]);

  const onSort = (header) => {
    const isAsc = orderBy === header && orderDirection === "asc";
    setOrderDirection(isAsc ? "desc" : "asc");
    setOrderBy(header);
    setProducts({ ...products, isFetching: true });
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setProducts({ ...products, isFetching: true });
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    setProducts({ ...products, isFetching: true });
  };

  const handleChangeFilterInput = (prop) => (event) => {
    setSearch({ ...search, [prop]: event.target.value });
    debouncedSearchHandler();
  };

  const exportData = (data) => {
    const today = new Date();
    exportToExcel(
      [],
      `Products-${today.toLocaleDateString()}`,
      true,
      convertDataToTableView(data, exportCells),
      setExportDisabled,
    );
    setIsDownloading(false);
  };

  const handleExportData = () => {
    setIsDownloading(true);
    if (products.items.length === products.count) {
      exportData(products.items);
    } else {
      getSellerProducts({
        params: {
          limit: products.count,
          ...search,
        },
        setFunc: ({ items: { results } }) => exportData(results),
      });
    }
  };

  const clearHandler = (input) => {
    setSearch({ ...search, [input]: "" });
    debouncedSearchHandler();
  };

  const clearAdornment = (input) =>
    getClearAdornment(input, clearHandler, search);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchHandler = useCallback(
    debounce(() => {
      setPage(0);
      setProducts((prev) => ({ ...prev, isFetching: true }));
    }, 500),
    [],
  );

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = function (evt) {
      const data = evt.target.result;
      const workbook = XLSX.read(data, {
        type: "binary",
        cellDates: true,
        dateNF: "yyyy-mm-dd",
      });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const rows = XLSX.utils.sheet_to_json(worksheet, {
        header: 1,
        raw: false,
        blankrows: false,
      });

      const normalizeHeader = (value) => value?.toString().trim().toLowerCase();
      const headerAliases = {
        sku: ["sku"],
        size: ["size"],
        length: ["length", "l"],
        width: ["width", "b"],
        height: ["height", "h"],
        weight: ["weight"],
        upc: ["upc", "barcode"],
      };

      const headerRowIndex = rows.findIndex((row) =>
        row.some((cell) => normalizeHeader(cell) === "sku"),
      );

      const columnIndexes = {};
      if (headerRowIndex !== -1) {
        rows[headerRowIndex].forEach((cell, idx) => {
          const normalized = normalizeHeader(cell);
          if (!normalized) return;

          Object.entries(headerAliases).forEach(([key, aliases]) => {
            if (columnIndexes[key] !== undefined) return;
            if (aliases.includes(normalized)) {
              columnIndexes[key] = idx;
            }
          });
        });
      }

      const requiredColumns = ["sku", "length", "width", "height", "weight"];
      let parsedValues = [];

      if (
        headerRowIndex !== -1 &&
        requiredColumns.every((key) => columnIndexes[key] !== undefined)
      ) {
        const cellHasValue = (value) => {
          if (value === undefined || value === null) return false;
          if (typeof value === "string") return value.trim() !== "";
          return true;
        };

        const getCellValue = (row, key) => {
          const columnIndex = columnIndexes[key];
          if (columnIndex === undefined) return undefined;
          const cell = row[columnIndex];
          if (typeof cell === "string") {
            return cell.trim();
          }
          return cell;
        };

        parsedValues = rows
          .slice(headerRowIndex + 1)
          .map((row) => ({
            sku: getCellValue(row, "sku"),
            upc: getCellValue(row, "upc"),
            length: getCellValue(row, "length"),
            width: getCellValue(row, "width"),
            height: getCellValue(row, "height"),
            weight: getCellValue(row, "weight"),
            short_size: getCellValue(row, "size"),
          }))
          .filter(
            (row) =>
              cellHasValue(row.sku) &&
              cellHasValue(row.length) &&
              cellHasValue(row.width) &&
              cellHasValue(row.height) &&
              cellHasValue(row.weight),
          );
      } else {
        parsedValues = XLSX.utils
          .sheet_to_json(worksheet, { raw: false })
          .filter(
            (value) =>
              value["SKU"] &&
              value["Length"] &&
              value["Width"] &&
              value["Height"] &&
              value["Weight"],
          )
          .map((value) => ({
            sku: value.SKU,
            upc: value.UPC,
            length: value.Length,
            width: value.Width,
            height: value.Height,
            weight: value.Weight,
            short_size: value.Size,
          }));
      }

      if (!parsedValues.length) {
        popupNotification({
          dispatch: layoutDispatch,
          message:
            "Please include SKU, Length (or L), Width (or B), Height (or H), and Weight columns in your Excel file.",
          status: "error",
        });
        return;
      }

      setProductData(
        parsedValues.map((value) => ({
          ...value,
          seller_id: seller_id,
        })),
      );
    };
    reader.readAsBinaryString(file);
    e.target.value = "";
  };

  const handleUpload = () => {
    setIsUploading(true);
    updateProducts({
      body: {
        products: productData,
      },
      responseSetter: (res, _) => {
        setIsUploading(false);
        setProductData([]);
        const upc_errors = [];
        if (res.results?.length) {
          res.results.forEach((r) => {
            if (r.error === "UPC already exists for another product.") {
              upc_errors.push({
                sku: r.sku,
                upc: r.upc,
                used_by: r.other_sku,
                error: r.error,
              });
            }
          });
        }
        if (upc_errors.length) {
          popupNotification({
            dispatch: layoutDispatch,
            message: "Products updated, but some products have UPC errors",
            status: "warning",
          });
          exportToExcel(
            upc_errors,
            "upc_errors",
            true,
            upc_errors.map((err) => ({
              SKU: err.sku,
              UPC: err.upc,
              "Used By (SKU)": err.used_by,
              Error: err.error,
            })),
          );
        } else if (res.success) {
          popupNotification({
            dispatch: layoutDispatch,
            message: t("successful_operation"),
            status: "success",
          });
        }
        setProducts((prev) => ({ ...prev, isFetching: true }));
      },
      setError: (err) => {
        popupNotification({
          dispatch: layoutDispatch,
          message: err,
        });
        setIsUploading(false);
      },
    });
  };

  const handleSave = () => {
    setIsUploading(true);

    const body = {
      products: [
        {
          sku: product?.sku,
          upc: product?.upc,
          length: product?.boxes?.[0].length,
          width: product?.boxes?.[0].width,
          height: product?.boxes?.[0].height,
          weight: product?.weight,
          short_size: product?.short_size,
          seller_id: seller_id,
        },
      ],
    };

    updateProducts({
      body: body,
      responseSetter: (res, _) => {
        setIsUploading(false);
        setProduct(null);
        if (res.success) {
          popupNotification({
            dispatch: layoutDispatch,
            message: t("successful_operation"),
            status: "success",
          });
        }
        setProducts((prev) => ({ ...prev, isFetching: true }));
      },
      setError: (err) => {
        popupNotification({
          dispatch: layoutDispatch,
          message: err,
        });
        setIsUploading(false);
      },
    });
  };

  return (
    <>
      <PageTitle
        window_title="Products"
        title={
          <>
            {t("products")}{" "}
            <IconButton
              onClick={() =>
                exportToExcel(
                  [
                    {
                      SKU: "",
                      UPC: "",
                      Length: "",
                      Width: "",
                      Height: "",
                      Weight: "",
                      Size: "",
                    },
                  ],
                  "product_upload_template",
                )
              }
            >
              <Download2 color="#FFF" />
            </IconButton>
          </>
        }
        multipleButtons={true}
        button={
          <>
            <Button
              endIcon={<ImportIcon color="white" />}
              variant="contained"
              color="primary-light"
              component="label"
            >
              Import
              <input
                hidden
                accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
                type="file"
                onChange={handleFileChange}
              />
            </Button>
            {permissions?.includes("product.export_data.product") &&
              products?.items?.length !== 0 && (
                <Button
                  endIcon={<ExportIcon color="white" />}
                  variant="contained"
                  size="medium"
                  color="primary-light"
                  disabled={isDownloading}
                  onClick={() => handleExportData()}
                >
                  {isDownloading ? "Downloading..." : "Export"}
                </Button>
              )}
          </>
        }
      />
      <InputContainer>
        <Grid container>
          <Grid item xs={12} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="sku"
              label={t("product_sku")}
              variant="outlined"
              value={search.sku}
              onChange={handleChangeFilterInput("sku")}
              isProductSKU={true}
              InputProps={clearAdornment("sku")}
              LeftIcon={QrCode2}
            />
          </Grid>
          <Grid item xs={5} sm={6} md={4} lg={2}>
            <FormattedTextField
              fullWidth
              id="upc"
              label={t("UPC")}
              variant="outlined"
              value={search.upc}
              onChange={handleChangeFilterInput("upc")}
              isProductSKU={true}
              InputProps={clearAdornment("upc")}
              LeftIcon={FolderSpecial}
            />
          </Grid>
        </Grid>
      </InputContainer>
      <Grid container spacing={4}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <DefaultTable
            headers={cells}
            headerCounts={headerCounts}
            data={products}
            rowsPerPage={rowsPerPage}
            page={page}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleExportData={handleExportData}
            disableExportData={true}
            sortActions={{ onSort, orderBy, orderDirection }}
            exportDisabled={exportDisabled}
            setExportDisabled={setExportDisabled}
            filters={search}
            rowOnClick={(product) => setProduct(product)}
          />
        </Grid>
      </Grid>
      {productData.length ? (
        <Dialog
          open={true}
          onClose={() => !isUploading && setProductData([])}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle> Import Products </DialogTitle>
          <DialogContent>
            <DefaultTable
              headers={[
                {
                  id: "sku",
                  label: "SKU",
                  render: (r, e) => r["sku"],
                },
                {
                  id: "upc",
                  label: "UPC",
                  render: (r, e) => r["upc"],
                },
                {
                  id: "length",
                  label: "Length",
                  render: (r, e) => r["length"],
                },
                {
                  id: "width",
                  label: "Width",
                  render: (r, e) => r["width"],
                },
                {
                  id: "height",
                  label: "Height",
                  render: (r, e) => r["height"],
                },
                {
                  id: "weight",
                  label: "Weight",
                  render: (r, e) => r["weight"],
                },
                {
                  id: "size",
                  label: "Size",
                  render: (r, e) => r["short_size"],
                },
              ]}
              data={{
                items: productData,
                count: productData.length,
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={handleUpload}
              disable={isUploading}
            >
              {isUploading ? <CircularProgress /> : "Upload"}
            </Button>
          </DialogActions>
        </Dialog>
      ) : null}

      {product && (
        <Dialog
          open={true}
          onClose={() => setProduct(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle> {product.sku} </DialogTitle>
          <DialogContent>
            <Grid container spacing={1} style={{ marginBottom: 20 }}>
              <Grid item xs={4}>
                <TextInput
                  id="UPC"
                  label="UPC"
                  fullWidth
                  value={product.upc || ""}
                  onChange={(e) => {
                    setProduct({ ...product, upc: e.target.value });
                  }}
                  hasBorder
                  LeftIcon={FolderSpecial}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  hasBorder
                  id="Length"
                  label="Length"
                  fullWidth
                  type="number"
                  value={product?.boxes?.[0].length || ""}
                  onChange={(e) => {
                    setProduct({
                      ...product,
                      boxes: [
                        { ...product.boxes?.[0], length: e.target.value },
                      ],
                    });
                  }}
                  LeftIcon={MergeType}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  hasBorder
                  id="Width"
                  label="Width"
                  fullWidth
                  type="number"
                  value={product.boxes?.[0].width || ""}
                  onChange={(e) => {
                    setProduct({
                      ...product,
                      boxes: [{ ...product.boxes?.[0], width: e.target.value }],
                    });
                  }}
                  LeftIcon={MergeType}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  hasBorder
                  id="Height"
                  label="Height"
                  fullWidth
                  type="number"
                  value={product.boxes?.[0].height || ""}
                  onChange={(e) => {
                    setProduct({
                      ...product,
                      boxes: [
                        { ...product.boxes?.[0], height: e.target.value },
                      ],
                    });
                  }}
                  LeftIcon={MergeType}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  type="number"
                  steps={1}
                  hasBorder
                  id="Weight"
                  label="Weight"
                  fullWidth
                  value={product.weight || ""}
                  onChange={(e) => {
                    setProduct({ ...product, weight: e.target.value });
                  }}
                  LeftIcon={Scale}
                />
              </Grid>
              <Grid item xs={4}>
                <TextInput
                  hasBorder
                  id="Size"
                  label="Size"
                  fullWidth
                  value={product.short_size || ""}
                  onChange={(e) => {
                    setProduct({ ...product, short_size: e.target.value });
                  }}
                  LeftIcon={CubeTransparent}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setProduct(null)}
            >
              Close
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleSave}
              disable={isUploading}
            >
              {isUploading ? <CircularProgress /> : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
